/**
 * <image-text-rows> — sections/image-text-rows.liquid
 *
 * Live's Prestige image slide: each row photograph eases in from its own side once it has loaded and reached the
 * viewport. `data-reveal="in_view"` waits until the image's top edge is a quarter of the viewport up from the bottom;
 * `on_load` starts it a little sooner, at 15%. Both play on screen, so the slide is seen.
 * section-image-text-rows.css hides the image only while this element carries data-reveal-armed, so without script
 * or with reduced motion the image simply shows.
 */
if (!customElements.get('image-text-rows')) {
  customElements.define(
    'image-text-rows',
    class ImageTextRows extends HTMLElement {
      observer = null;
      pending = new Set();

      connectedCallback() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const images = this.querySelectorAll('.image-text-rows__image--reveal');
        if (images.length === 0) return;

        // Started this early, the slide played above the fold of the next scroll and was never seen
        const rootMargin = this.dataset.reveal === 'in_view' ? '0px 0px -25% 0px' : '0px 0px -15% 0px';
        this.observer = new IntersectionObserver(this.onIntersect, { rootMargin });
        this.setAttribute('data-reveal-armed', '');

        for (const image of images) {
          this.observer.observe(image);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;

        for (const image of this.pending) {
          image.removeEventListener('load', this.onImageSettled);
          image.removeEventListener('error', this.onImageSettled);
        }

        this.pending.clear();
      }

      onIntersect = (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const image = entry.target;
          this.observer.unobserve(image);

          if (image.complete) {
            this.reveal(image);
          } else {
            this.pending.add(image);
            image.addEventListener('load', this.onImageSettled);
            image.addEventListener('error', this.onImageSettled);
          }
        }
      };

      onImageSettled = (event) => {
        const image = event.currentTarget;
        image.removeEventListener('load', this.onImageSettled);
        image.removeEventListener('error', this.onImageSettled);
        this.pending.delete(image);
        this.reveal(image);
      };

      reveal(image) {
        // Resolve the hidden start style first, or a cached image skips the transition
        getComputedStyle(image).opacity;
        image.classList.add('is-revealed');
      }
    },
  );
}
